/**
 * CartChip — compact "cart" icon shown in the header for signed-in users.
 * Shows a numeric badge when the cart has items. Refreshes on mount and
 * on the global `sp:cart-changed` event fired by product cards.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export function CartChip() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user?.id) { setCount(0); return; }
    try {
      const { data } = await http.get("/cart");
      setCount(data?.totals?.count || 0);
    } catch { setCount(0); }
  }, [user?.id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const on = () => refresh();
    window.addEventListener("sp:cart-changed", on);
    return () => window.removeEventListener("sp:cart-changed", on);
  }, [refresh]);

  return (
    <Link to="/cart" data-testid="header-cart-chip" className="relative wofy-btn-ghost !px-2 inline-flex items-center" title={count ? `${count} item${count === 1 ? "" : "s"} in cart` : "Cart"}>
      <ShoppingBag className="w-4 h-4" />
      {count > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold text-white grid place-items-center leading-none"
          style={{ background: "var(--wofy-primary)" }}
          data-testid="cart-chip-count"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}
